import { Link } from "@tanstack/react-router";
import { LuExternalLink, LuHistory, LuMessageSquareText, LuScale } from "react-icons/lu";
import { LuShieldUser } from "../components/icons";
import type { IconType } from "react-icons";
import type { ReactNode } from "react";
import { Box, Card, Heading, HStack, Icon, SimpleGrid, Stack, Text } from "@chakra-ui/react";
import { PageHeader } from "../components/PageHeader";
import { LoadingCard } from "../components/LoadingCard";
import { useViewerAccess } from "@/lib/access";

const convexDashboardUrl = process.env.PUBLIC_CONVEX_DASHBOARD_URL as string | undefined;

function EntryCard({ icon, title, description, note }: {
  icon: IconType;
  title: string;
  description: string;
  note?: ReactNode;
}) {
  return (
    <Card.Root borderWidth="1px" borderColor="border" h="full" _hover={{ borderColor: "blue.300" }} transition="colors 0.15s">
      <Card.Body gap={2} p={5}>
        <HStack gap={3}>
          <Icon as={icon} boxSize={5} color="blue.500" />
          <Heading size="md">{title}</Heading>
        </HStack>
        <Text fontSize="sm" color="fg.muted">{description}</Text>
        {note ? <Text fontSize="xs" color="fg.muted">{note}</Text> : null}
      </Card.Body>
    </Card.Root>
  );
}

/**
 * The Administration section: HR's and IT's pages, in one place.
 *
 * Each page keeps its own gate -- the reconciliation report is HR's as well as
 * IT's, the audit log the sys admin's alone -- so this lists whichever of them
 * the viewer may open rather than gating the section as a whole.
 */
export function AdministrationPage() {
  const access = useViewerAccess();

  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title="Administration"
        description="Pages for HR and system admins."
        breadcrumbs={[{ label: "CeMMbase", to: "/" }, { label: "Administration" }]}
      />
      <Stack gap={8} px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>
        {access === undefined ? (
          <LoadingCard label="Loading…" />
        ) : !access.isDirectoryAdmin && !access.isSysAdmin ? (
          <Text fontSize="sm" color="fg.muted">Nothing here is open to you: these pages are for HR and system administrators.</Text>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap={4}>
            {access.isDirectoryAdmin ? (
              <Link to="/admin/reconciliation">
                <EntryCard
                  icon={LuScale}
                  title="Reconciliation"
                  description="Where the organisational record and the directory feeds disagree, and what is waiting on a decision."
                />
              </Link>
            ) : null}
            {access.isSysAdmin ? (
              <Link to="/admin/audit-log">
                <EntryCard
                  icon={LuHistory}
                  title="Audit log"
                  description="Who did what across CeMMbase, newest first: a step submitted, a deadline moved, an account that left the directory."
                />
              </Link>
            ) : null}
            {access.isSysAdmin ? (
              <Link to="/admin/roles">
                <EntryCard
                  icon={LuShieldUser}
                  title="Roles"
                  description="Who the system, activity, HR and grants admins are, and changing that."
                />
              </Link>
            ) : null}
            {access.isSysAdmin ? (
              <Link to="/admin/feedback">
                <EntryCard
                  icon={LuMessageSquareText}
                  title="Feedback"
                  description="What people sent with “Send feedback”, and the page they sent it from."
                />
              </Link>
            ) : null}
            {access.isSysAdmin && convexDashboardUrl ? (
              <a href={convexDashboardUrl} target="_blank" rel="noreferrer">
                <EntryCard
                  icon={LuExternalLink}
                  title="Convex dashboard"
                  description="The backend's own console: data, functions, logs and environment variables."
                  note="Opens in a new tab. Reachable from the intranet or VPN only, and asks for the deployment's admin key."
                />
              </a>
            ) : null}
          </SimpleGrid>
        )}
      </Stack>
    </Box>
  );
}
